/**
 * Comment le contrôle du transport se dit à l'écran.
 *
 * *Demande de Chris, 04/09/2026 : « fournir un contrôle du nombre d'heures paramétré […] sur le
 * tonnage il faut calculer le nombre de tours ».*
 *
 * Le calcul vit dans le moteur ; ce module ne fait que **choisir les mots**. Séparé du dessin
 * pour être éprouvé seul, et parce que la phrase compte autant que le nombre : un contrôle qui
 * annonce un écart sans dire ce qu'il a supposé ne se vérifie pas, et un responsable qui ne peut
 * pas vérifier finit par ne plus lire.
 */
import { dec, formaterNombre } from "@planif/moteur";
import type { ControleDuTransport } from "./api";

/** Ce que chaque incapacité dit, en clair, dans l'ordre où elle se lit. */
const MOTS_DES_INCAPACITES: Readonly<Record<string, string>> = {
  mode_non_renseigne: "qui achemine n'est pas renseigné — à compléter sur la ligne",
  pas_par_nos_soins: "livré par le fournisseur, aucun de nos camions n'est mobilisé",
  unite_non_convertible: "unité non convertible en tonnes : aucun voyage calculable",
  charge_utile_inconnue: "charge utile du matériel de transport inconnue",
  distance_inconnue: "distance de la carrière au chantier non renseignée",
  // Le poste porte plusieurs attelages : la pastille doit dire lequel emporte son tonnage.
  // La phrase dit OÙ corriger, parce que le geste n'est pas sur cet écran-ci.
  attelage_non_designe:
    "une pastille de tonnage ne dit pas quel attelage l'emporte — à choisir sur la pastille, " +
    "dans la contre-étude",
};

/** Le motif, en clair, ou la clé elle-même si un jour un motif neuf n'a pas de phrase. */
export function direLIncapacite(motif: string): string {
  return MOTS_DES_INCAPACITES[motif] ?? motif;
}

/**
 * Le contrôle a-t-il quelque chose à dire ?
 *
 * Un contrôle « pas par nos soins » a bien quelque chose à dire — *elle occupe la piste et le
 * déchargement* (décision du 01/09) — donc il s'affiche. Ce qui ne s'affiche pas est un contrôle
 * absent, sur une ligne qui n'est pas une matière.
 */
export function aQuelqueChoseADire(controle: ControleDuTransport | null): controle is ControleDuTransport {
  return controle !== null;
}

/**
 * L'écart en heures, dit dans le sens du métier.
 *
 * **Le signe se traduit en mots, jamais laissé au lecteur.** « +14,2 h » demande de savoir si le
 * plus est en faveur du chiffrage ou contre lui ; « il manque 14,2 h » ne le demande pas. C'est
 * la même raison qui fait dire « avancé » et « repoussé » sur un décalage plutôt que ± un nombre.
 */
export function direLEcart(controle: ControleDuTransport): string | null {
  if (controle.ecartHeures === null) return null;
  const ecart = Number(controle.ecartHeures);
  // Un quart d'heure d'écart sur un chiffrage de camion n'est pas un écart : c'est la précision
  // de la vitesse moyenne. Le signaler ferait crier au loup sur toutes les lignes justes.
  if (Math.abs(ecart) < 0.25) return "conforme au calcul";
  const heures = formaterNombre(Math.abs(ecart).toFixed(4), 1);
  return ecart > 0 ? `il manque ${heures} h au chiffrage` : `${heures} h de plus que le calcul`;
}

/** Le ton de l'écart : c'est lui qui décide de la couleur. */
export function tonDeLEcart(controle: ControleDuTransport): "conforme" | "ecart" | "neutre" {
  if (controle.ecartHeures === null) return "neutre";
  return Math.abs(Number(controle.ecartHeures)) < 0.25 ? "conforme" : "ecart";
}

/** Des heures décimales dites en minutes, quand elles sont trop courtes pour se lire en heures. */
function direUneDuree(heures: string): string {
  const valeur = Number(heures);
  // Sous l'heure et demie, le métier compte en minutes — *« 17 tours de 20 minutes »*. Au-delà,
  // les minutes deviennent illisibles : « 145 min » ne dit rien, « 2,4 h » se compare à une
  // journée.
  if (valeur < 1.5) return `${String(Math.round(valeur * 60))} min`;
  return `${formaterNombre(heures, 1)} h`;
}

/**
 * Le calcul en toutes lettres, terme par terme.
 *
 * *Demande de Chris, 04/09/2026 : « sois plus factuel — 80 tonnes = 17 tours de 20 minutes qui
 * correspondent aux heures évoquées ».*
 *
 * **Un total d'heures ne se vérifie pas ; un total dont on voit les termes se vérifie de tête.**
 * La phrase suit donc exactement la chaîne du calcul, dans l'ordre où on la refait à la main :
 *
 *     80 T ÷ 17 T = 5 tours · 1 tour = 2,4 h (95,8 km A/R à 50 km/h + 30 min) = 12,1 h
 *
 * Chaque terme est corrigible séparément, et c'est là tout l'intérêt : la distance se corrige à
 * la table des distances, la manœuvre au paramétrage, la charge utile au référentiel des
 * attelages. Un écran qui n'affiche que « 12,1 h attendues » n'apprend pas où corriger.
 */
export function direLeCalcul(controle: ControleDuTransport): string | null {
  if (controle.voyages === null || controle.tonnage === null || controle.chargeUtile === null) {
    return null;
  }
  const tonnes = formaterNombre(controle.tonnage, 1);
  const charge = formaterNombre(controle.chargeUtile, 1);
  const tours = `${String(controle.voyages)} tour${controle.voyages > 1 ? "s" : ""}`;
  const debut = `${tonnes} T ÷ ${charge} T = ${tours}`;

  if (controle.parVoyage !== null) {
    const v = controle.parVoyage;
    /*
      La manœuvre n'est mentionnée que si elle compte. Elle vaut zéro depuis le 04/09 — le
      chiffrage de Chris se vérifie sur la route seule — et « + 0 min de manœuvre » sur chaque
      ligne serait du bruit qui ferait chercher un terme qui n'existe pas.
    */
    const manoeuvre =
      Number(v.manoeuvreHeures) > 0 ? ` + ${direUneDuree(v.manoeuvreHeures)} de manœuvre` : "";
    const detail = `${formaterNombre(v.kmAllerRetour, 1)} km aller-retour${manoeuvre}`;
    const total =
      controle.heuresAttendues === null
        ? ""
        : ` = ${formaterNombre(controle.heuresAttendues, 1)} h attendues`;
    /*
      **Les heures chiffrées sont dites, et rattachées au JOUR.**

      *« Le 1er jour on porte 160 t avec 2 porteurs 8×4 et le 2e jour on porte 80 t »* (Chris,
      04/09/2026) : le contrôle se fait par journée, et la phrase doit le dire. Ces heures sont
      celles de toutes les rotations du poste ce jour-là, tous exemplaires réunis — donc 16 h
      pour une rotation de 8 h à deux porteurs, ce que le tableau affiche déjà à côté.
    */
    const chiffrees = ` · ${formaterNombre(controle.heuresChiffrees, 1)} h chiffrées ce jour`;
    return `${debut} · 1 tour = ${direUneDuree(v.heures)} (${detail})${total}${chiffrees}`;
  }

  /*
    Sans distance connue, on retourne le calcul : ce que le chiffrage a supposé pour un tour.
    C'est le seul contrôle disponible aujourd'hui sur la plupart des lignes — six des huit sites
    du groupe n'ont pas leurs kilométrages complets — et c'est celui qui parle le plus au métier,
    parce qu'un chauffeur sait ce que dure un tour chez lui.
  */
  if (controle.parVoyageChiffre !== null) {
    /*
      **La distance se dit ALLER-RETOUR ici, et en aller simple partout ailleurs.**

      C'est ainsi que le métier compte un tour — *« 15 voyages × 77 km = 1155 km à 50 km/h »*
      (Chris, 04/09/2026) —, alors que la table des distances porte l'aller simple. Afficher
      36,7 km en réponse à quelqu'un qui en attend 77 ferait chercher une erreur de facteur deux
      là où les deux calculs disent la même chose. Les deux sont donc écrits, dans cet ordre.
    */
    const suppose =
      controle.distanceImplicite === null
        ? ""
        : ` soit ${formaterNombre(dec(controle.distanceImplicite).times(2).toFixed(), 1)} km par tour` +
          ` (${formaterNombre(controle.distanceImplicite, 1)} km de trajet)`;
    return (
      `${debut} · les ${formaterNombre(controle.heuresChiffrees, 1)} h chiffrées donnent ` +
      `${direUneDuree(controle.parVoyageChiffre)} par tour${suppose}`
    );
  }
  return debut;
}

/**
 * Ce que la liste des attelages d'une pastille de tonnage affiche, et ce qu'elle propose.
 *
 * ## Pourquoi cette règle est sortie du dessin
 *
 * *« Pourquoi dans MAILLAS je ne vois pas comme dans CAMPET le champ couplage de camion sur le
 * détail de la ligne des matériaux ? Même s'il y a un seul couplage tu peux faire apparaître le
 * champ […] et tu peux du coup renseigner directement le bon couplage quand il y en a qu'un »*
 * (Chris, 10/09/2026).
 *
 * La liste ne s'affichait qu'au-delà d'un attelage. Le motif — « un poste qui n'en porte qu'un
 * n'a rien à désigner » — est vrai du **calcul** et faux de l'**écran** : un champ absent ne dit
 * pas « un seul attelage », il ne dit rien, et deux chantiers voisins n'avaient pas la même
 * fiche sans que rien ne l'explique.
 *
 * ## Les trois réponses, et pourquoi elles diffèrent
 *
 * | Attelages | Affiché | Option vide |
 * |---|---|---|
 * | aucun | rien du tout | — |
 * | un seul | lui, présélectionné | **non** : il n'y a rien à ne pas choisir |
 * | plusieurs, non désigné | le vide | oui, et c'est une question |
 * | plusieurs, désigné | le choix du conducteur | oui, pour pouvoir le défaire |
 *
 * Le vide n'est proposé qu'au-delà d'un attelage : sur un poste qui n'en porte qu'un, inviter à
 * défaire une réponse certaine ferait fabriquer le refus `attelage_non_designe` à la main.
 *
 * **Ce que cette fonction ne fait pas : écrire.** Elle choisit ce qui s'affiche. Le figement en
 * base a lieu au clic sur « organiser le transport », côté serveur, et seulement sur les
 * pastilles que personne n'a désignées — voir `organiserLeTransportDeLaVersion`.
 */
export function attelageAffiche(
  attelages: readonly { rang: number }[],
  designe: number | null | undefined,
): { readonly valeur: string; readonly avecOptionVide: boolean } {
  const avecOptionVide = attelages.length > 1;
  if (designe !== null && designe !== undefined) {
    return { valeur: String(designe), avecOptionVide };
  }
  // Un seul attelage : c'est lui que le calcul retiendra, donc c'est lui qu'on montre.
  const seul = attelages.length === 1 ? attelages[0]?.rang : undefined;
  return { valeur: seul === undefined ? "" : String(seul), avecOptionVide };
}
